import { useEffect, useState } from "react";
import WithAuth from "../../hoc/WithAuth";
import { UndoOutlined, UnlockOutlined, LockOutlined } from "@ant-design/icons";
import { Button, Image, message, Modal, Select, Table } from "antd";
import { getStatusColor } from "../../util/statusColor";
import {
  useLockUnlockUser,
  useMyUsersList,
  useResetPassword,
  useUserUpdateStatus,
} from "../../api/users";
import TableAction from "../../components/TableAction";
import CreateUserModal from "../../components/modals/CreateUserModal";

type UserType = {
  email: string;
  fullName: string;
  id: number;
  isActive: string;
  isLocked: number;
  organization: string;
  phoneNo: string;
  role: string;
  username: string;
};
const SuperAdminPage = () => {
  const [openCreate, setOpenCreate] = useState(false);
  const [status, setStatus] = useState<"ACTIVE" | "INACTIVE" | undefined>();
  const [selectedId, setSelectedId] = useState("");
  const [open, setOpen] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 10;

  const { mutateAsync: updateStatus, isPending } = useUserUpdateStatus();
  const { data: myUsers } = useMyUsersList({
    search: searchText || undefined,
    page: currentPage - 1,
    size: pageSize,
  });

  const { refetch: lockUnlockUser, isRefetching: lockIsRefetching } =
    useLockUnlockUser(selectedId);
  const {
    refetch: resetPassword,
    isLoading: resetLoading,
    isRefetching: resetPasswordRefetching,
  } = useResetPassword(selectedId);
  const [admins, setAdmins] = useState<UserType[]>([]);

  useEffect(() => {
    if (
      myUsers &&
      myUsers.data &&
      myUsers.data.data &&
      myUsers.data.data.length > 0
    ) {
      const _admins = myUsers.data.data.filter(
        (item: any) => item.role === "ROLE_KFT_SUPER_ADMIN"
      );

      setAdmins(_admins);
    }
  }, [myUsers]);

  const resetUserPassword = async () => {
    if (selectedId) {
      const result = await resetPassword();

      setSelectedId("");

      if (
        result &&
        result.data &&
        result.data.data &&
        result.data.data.status === "success"
      ) {
        message.success("Password reseted successfully.");
      } else {
        message.error("Operation failed.");
      }
    }
  };

  const _lockUnlockUser = async () => {
    if (selectedId) {
      const result = await lockUnlockUser();

      setSelectedId("");

      if (
        result &&
        result.data &&
        result.data.data &&
        result.data.data.status === "success"
      ) {
        const msg =
          result.data.data.data.isLocked === 1
            ? "User locked successfully."
            : "User unlocked successfully.";
        message.success(msg);
      } else {
        message.error("Operation failed.");
      }
    }
  };

  const onUpdate = async () => {
    if (status) {
      try {
        const result = await updateStatus({
          selectedId,
          status,
        } as any);

        if (result.status === 200) {
          message.success("User status updated successfully.");
        } else {
          message.error("Operation failed");
        }
      } catch (err: any) {
        message.error("Operation failed.");
      } finally {
        setStatus(undefined);
        setOpen(false);
      }
    }
  };

  const usersColumn = [
    {
      dataIndex: "fullName",
      title: "Full name",
      render: (text: string, record: any, index: number) => (
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <div>
            {record.avatar_url && (
              <Image
                src={record.avatar_url}
                style={{ width: 50, aspectRatio: 1, borderRadius: 50 }}
              />
            )}
          </div>
          <div>{record.fullName}</div>
        </div>
      ),
    },
    {
      dataIndex: "email",
      title: "Email",
    },
    {
      dataIndex: "phoneNo",
      title: "Phone number",
    },
    {
      dataIndex: "role",
      title: "Role",
    },
    {
      dataIndex: "isActive",
      title: "Status",
      render: (status: string, record: any) => {
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              width: 100,
              paddingTop: 6,
              paddingBottom: 6,
              borderRadius: 8.5,
              color: getStatusColor(status === "1" ? "ACTIVE" : "INACTIVE"),
              backgroundColor: `${getStatusColor(
                status === "1" ? "ACTIVE" : "INACTIVE"
              )}44`,
            }}
          >
            {status === "1" ? "ACTIVE" : "INACTIVE"}
          </div>
        );
      },
    },
    {
      title: "Actions",
      render: (status: number, record: any, index: number) => (
        <div style={{ display: "flex", gap: 12 }}>
          <Button
            type="primary"
            // disabled={record.id === user?.userId}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedId(record.id);
              setStatus(status === 1 ? "ACTIVE" : "INACTIVE");
              setOpen(true);
            }}
          >
            Update Status
          </Button>

          <Button
            key={record.id}
            loading={
              resetLoading ||
              (resetPasswordRefetching && selectedId === record.id)
            }
            type="primary"
            icon={<UndoOutlined />}
            onClick={async (e) => {
              e.stopPropagation();
              setSelectedId(record.id);

              resetUserPassword();
            }}
            style={{ backgroundColor: "#f5222d" }}
          >
            Reset password
          </Button>

          <Button
            key={record.id}
            loading={lockIsRefetching && selectedId === record.id}
            type="primary"
            icon={record.isLocked === 0 ? <UnlockOutlined /> : <LockOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedId(record.id);

              _lockUnlockUser();
            }}
            style={{
              backgroundColor: record.isLocked === 0 ? "#f55157" : "#87faa6",
            }}
          >
            {`${record.isLocked === 0 ? "Lock" : "Unlock"}`}
          </Button>
        </div>
      ),
    },
  ];
  return (
    <div
      className="px-2 md:bg-white rounded-xl md:px-8 md:mt-[25px] md:pt-[22px] bg-transparent md:bg-whit"
      style={{
        borderRadius: 8.5,
        marginTop: 25,
      }}
    >
      <Modal
        destroyOnClose={true}
        title={<div style={{ color: "black" }}>Confirmation</div>}
        open={open}
        centered
        onOk={onUpdate}
        okButtonProps={{
          disabled: isPending,
          loading: isPending,
        }}
        onCancel={() => {
          setStatus(undefined);
          setOpen(false);
        }}
        closable
        style={{ color: "black" }}
      >
        <Select
          value={status}
          placeholder="Update status"
          style={{ width: "100%" }}
          onChange={(val: any) => setStatus(val)}
          options={["ACTIVE", "INACTIVE"].map((_status: string) => {
            return { label: _status, value: _status };
          })}
        />
      </Modal>
      {/* <CreateUserModal
        open={openCreate}
        hideModal={() => setOpenCreate(false)}
      /> */}

      <TableAction
        setSearchText={() => {}}
        onCreateNew={() => setOpenCreate(true)}
      />
      <Table
        style={{
          cursor: "pointer",
          position: "relative",
          zIndex: 100,
          overflowY: "hidden",
        }}
        scroll={{ y: 600 }}
        columns={usersColumn}
        dataSource={admins}
      />
    </div>
  );
};

export default WithAuth(SuperAdminPage);
